/**
 * DentalPlanCard — „Моят план при зъболекар".
 * Категория риск по CAMBRA/EFP → интервали за преглед, почистване, снимка,
 * флуорид и оглед на устната кухина; отбелязване на извършена дата и
 * разпечатка за зъболекаря.
 * Данни: GET /api/dental-intelligence/plan · POST /exam · GET /print
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  CalendarCheck, AlertTriangle, CheckCircle2, Clock, Printer, Loader2, Plus,
} from "lucide-react";
import { useAuth } from "@/App";

const CAT_STYLE = {
  low: "bg-emerald-500/15 text-emerald-600",
  moderate: "bg-amber-500/15 text-amber-600",
  high: "bg-orange-500/20 text-orange-600",
  extreme: "bg-red-500/20 text-red-600",
};

export default function DentalPlanCard({ isDark = false }) {
  const { API } = useAuth();
  const [d, setD] = useState(null);
  const [logFor, setLogFor] = useState(null);
  const [date, setDate] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/dental-intelligence/plan`);
      setD(data);
    } catch { setD(null); }
  }, [API]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!date) { toast.error("Изберете дата"); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/dental-intelligence/exam`,
        { code: logFor, date });
      setD(data);
      setLogFor(null); setDate("");
      toast.success("Записано — планът е обновен");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Записването не успя");
    } finally { setSaving(false); }
  };

  const openPrint = async () => {
    try {
      const res = await axios.get(`${API}/dental-intelligence/print`,
        { responseType: "blob" });
      window.open(URL.createObjectURL(res.data), "_blank");
    } catch { toast.error("Разпечатката не се отвори"); }
  };

  if (!d) return null;
  const card = isDark ? "bg-slate-900/60 border-slate-800" : "bg-white border-slate-200";

  return (
    <div className={`rounded-2xl border p-4 ${card}`} data-testid="dental-plan-card">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[12px] font-extrabold inline-flex items-center gap-1.5">
            <CalendarCheck className="w-4 h-4 text-cyan-500" />
            Моят план при зъболекар
          </div>
          <div className={`mt-1 inline-block px-2 py-0.5 rounded-full text-[11px] font-bold
                          ${CAT_STYLE[d.category] || CAT_STYLE.moderate}`}
            data-testid="dental-plan-category">
            риск: {d.category_bg}
          </div>
          <div className="text-[11px] opacity-75 mt-1" data-testid="dental-plan-reasons">
            {d.reasons_bg.join(" · ")}
          </div>
        </div>
        <button onClick={openPrint}
          className="px-2.5 py-1.5 rounded-lg text-[11px] font-bold inline-flex items-center
                     gap-1 bg-cyan-500/15 text-cyan-600 hover:bg-cyan-500/25 shrink-0"
          data-testid="dental-plan-print">
          <Printer className="w-3.5 h-3.5" /> За зъболекаря
        </button>
      </div>

      <div className="text-[10px] opacity-60 mt-1.5">{d.intervals_bg}</div>

      <div className="mt-3 space-y-2" data-testid="dental-plan-items">
        {d.plan.map((i) => (
          <div key={i.code}
            className={`p-3 rounded-xl border ${isDark ? "border-white/10" : "border-slate-200"}`}
            data-testid={`dental-plan-item-${i.code}`}>
            <div className="flex items-start gap-2">
              {i.status === "overdue" || i.status === "unknown"
                ? <AlertTriangle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                : i.status === "due"
                  ? <Clock className="w-4 h-4 text-amber-500 shrink-0 mt-0.5" />
                  : <CheckCircle2 className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />}
              <div className="flex-1 min-w-0">
                <div className="text-[12px] font-bold">{i.title_bg}</div>
                <div className="text-[11px] opacity-80">
                  на всеки {i.every_months} месеца · {i.status_bg}
                </div>
                <div className="text-[10px] opacity-60 mt-0.5">{i.why_bg}</div>
              </div>
              <button onClick={() => { setLogFor(i.code); setDate(""); }}
                className="px-2 py-1 rounded-lg text-[10px] font-bold inline-flex items-center
                           gap-1 bg-cyan-500/15 text-cyan-600 hover:bg-cyan-500/25 shrink-0"
                data-testid={`dental-plan-log-${i.code}`}>
                <Plus className="w-3 h-3" /> дата
              </button>
            </div>
            {logFor === i.code && (
              <div className="mt-2 flex items-center gap-2">
                <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                  className={`flex-1 px-2 py-1.5 rounded-lg text-[12px] border ${
                    isDark ? "bg-slate-800 border-slate-700" : "bg-white border-slate-300"}`}
                  data-testid={`dental-plan-date-${i.code}`} />
                <button onClick={save} disabled={saving}
                  className="px-3 py-1.5 rounded-lg text-[11px] font-bold text-white
                             bg-cyan-600 hover:bg-cyan-500 disabled:opacity-50
                             inline-flex items-center gap-1"
                  data-testid={`dental-plan-save-${i.code}`}>
                  {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : null} Запази
                </button>
              </div>
            )}
          </div>
        ))}
      </div>

      {!!d.modifiers?.length && (
        <div className="mt-3 space-y-1.5" data-testid="dental-plan-modifiers">
          <div className="text-[10px] font-bold uppercase opacity-60">
            Отчетено от здравния Ви профил
          </div>
          {d.modifiers.map((m, i) => (
            <div key={i} className="flex items-start gap-1.5 text-[11px]">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-500 shrink-0 mt-0.5" />
              <span>{m.note_bg}</span>
            </div>
          ))}
        </div>
      )}

      <div className="mt-3" data-testid="dental-plan-steps">
        <div className="text-[10px] font-bold uppercase opacity-60">Всекидневни стъпки</div>
        {d.next_steps_bg.map((s, i) => (
          <div key={i} className="text-[11px]">• {s}</div>
        ))}
      </div>

      <div className="text-[9px] opacity-50 mt-3">{d.disclaimer_bg}</div>
    </div>
  );
}
